import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface JobCompletionChecklistProps {
  jobId: string;
  orgId: string;
  onComplete?: () => void;
}

interface ChecklistData {
  id?: string;
  final_photos_uploaded: boolean;
  materials_used: any;
  materials_leftover: any;
  cleanup_confirmed: boolean;
  walkthrough_completed: boolean;
  punch_list_complete: boolean;
  crew_notes: string;
  completed_at: string | null;
}

export const JobCompletionChecklist = ({ jobId, orgId, onComplete }: JobCompletionChecklistProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistData>({
    final_photos_uploaded: false,
    materials_used: {},
    materials_leftover: {},
    cleanup_confirmed: false,
    walkthrough_completed: false,
    punch_list_complete: false,
    crew_notes: "",
    completed_at: null,
  });
  const [materialsUsedText, setMaterialsUsedText] = useState("");
  const [materialsLeftoverText, setMaterialsLeftoverText] = useState("");

  useEffect(() => {
    loadChecklist();
  }, [jobId, orgId]);

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("job_completion_checklists")
        .select("*")
        .eq("job_id", jobId)
        .eq("org_id", orgId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setChecklist(data);
        setMaterialsUsedText(typeof data.materials_used === 'object' ? JSON.stringify(data.materials_used, null, 2) : "");
        setMaterialsLeftoverText(typeof data.materials_leftover === 'object' ? JSON.stringify(data.materials_leftover, null, 2) : "");
      }
    } catch (error) {
      console.error("Error loading checklist:", error);
      toast.error("Failed to load checklist");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const checklistData = {
        job_id: jobId,
        org_id: orgId,
        final_photos_uploaded: checklist.final_photos_uploaded,
        materials_used: materialsUsedText ? JSON.parse(materialsUsedText) : {},
        materials_leftover: materialsLeftoverText ? JSON.parse(materialsLeftoverText) : {},
        cleanup_confirmed: checklist.cleanup_confirmed,
        walkthrough_completed: checklist.walkthrough_completed,
        punch_list_complete: checklist.punch_list_complete,
        crew_notes: checklist.crew_notes,
        completed_at: null,
        completed_by: user?.id,
      };

      const { error } = await supabase
        .from("job_completion_checklists")
        .upsert(checklistData, {
          onConflict: "job_id",
        });

      if (error) throw error;

      toast.success("Checklist saved");
      await loadChecklist();
    } catch (error: any) {
      console.error("Error saving checklist:", error);
      toast.error(error.message || "Failed to save checklist");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    // Check if all items are checked
    const allComplete =
      checklist.final_photos_uploaded &&
      checklist.cleanup_confirmed &&
      checklist.walkthrough_completed &&
      checklist.punch_list_complete;

    if (!allComplete) {
      toast.error("Please complete all checklist items before marking as complete");
      return;
    }

    try {
      setSaving(true);

      const checklistData = {
        job_id: jobId,
        org_id: orgId,
        final_photos_uploaded: checklist.final_photos_uploaded,
        materials_used: materialsUsedText ? JSON.parse(materialsUsedText) : {},
        materials_leftover: materialsLeftoverText ? JSON.parse(materialsLeftoverText) : {},
        cleanup_confirmed: checklist.cleanup_confirmed,
        walkthrough_completed: checklist.walkthrough_completed,
        punch_list_complete: checklist.punch_list_complete,
        crew_notes: checklist.crew_notes,
        completed_at: new Date().toISOString(),
        completed_by: user?.id,
      };

      const { error } = await supabase
        .from("job_completion_checklists")
        .upsert(checklistData, {
          onConflict: "job_id",
        });

      if (error) throw error;

      toast.success("Job completion verified! ✓", {
        description: "All quality checks passed. Ready to invoice.",
      });
      
      await loadChecklist();
      onComplete?.();
    } catch (error: any) {
      console.error("Error completing checklist:", error);
      toast.error(error.message || "Failed to complete checklist");
    } finally {
      setSaving(false);
    }
  };

  const isComplete = checklist.completed_at !== null;
  const progressCount = [
    checklist.final_photos_uploaded,
    checklist.cleanup_confirmed,
    checklist.walkthrough_completed,
    checklist.punch_list_complete,
  ].filter(Boolean).length;
  const progressPercent = Math.round((progressCount / 4) * 100);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isComplete ? "border-green-500" : ""}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {isComplete ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Job Completion Verified
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  Quality Assurance Checklist
                </>
              )}
            </CardTitle>
            <CardDescription>
              {isComplete
                ? `Completed on ${new Date(checklist.completed_at!).toLocaleDateString()}`
                : "Complete all items before moving to invoicing"}
            </CardDescription>
          </div>
          {!isComplete && (
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{progressPercent}%</div>
              <div className="text-xs text-muted-foreground">{progressCount} of 4 complete</div>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Main Checklist Items */}
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="final-photos"
              checked={checklist.final_photos_uploaded}
              onCheckedChange={(checked) =>
                setChecklist({ ...checklist, final_photos_uploaded: checked as boolean })
              }
              disabled={isComplete}
            />
            <div className="flex-1">
              <Label htmlFor="final-photos" className="text-base font-medium cursor-pointer">
                Final Photos Uploaded
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Drone or ground photos of completed work
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="cleanup"
              checked={checklist.cleanup_confirmed}
              onCheckedChange={(checked) =>
                setChecklist({ ...checklist, cleanup_confirmed: checked as boolean })
              }
              disabled={isComplete}
            />
            <div className="flex-1">
              <Label htmlFor="cleanup" className="text-base font-medium cursor-pointer">
                Cleanup Confirmation
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Site cleaned, debris removed, materials secured
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="walkthrough"
              checked={checklist.walkthrough_completed}
              onCheckedChange={(checked) =>
                setChecklist({ ...checklist, walkthrough_completed: checked as boolean })
              }
              disabled={isComplete}
            />
            <div className="flex-1">
              <Label htmlFor="walkthrough" className="text-base font-medium cursor-pointer">
                Property Walk-Around Complete
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Final inspection with customer or team lead
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Checkbox
              id="punch-list"
              checked={checklist.punch_list_complete}
              onCheckedChange={(checked) =>
                setChecklist({ ...checklist, punch_list_complete: checked as boolean })
              }
              disabled={isComplete}
            />
            <div className="flex-1">
              <Label htmlFor="punch-list" className="text-base font-medium cursor-pointer">
                Punch-List Complete
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                All outstanding items addressed and resolved
              </p>
            </div>
          </div>
        </div>

        {/* Materials Section */}
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="materials-used">Materials Used (Optional)</Label>
            <Textarea
              id="materials-used"
              placeholder='{"shingles": "30 bundles", "nails": "2 boxes"}'
              value={materialsUsedText}
              onChange={(e) => setMaterialsUsedText(e.target.value)}
              disabled={isComplete}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">JSON format or plain text</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="materials-leftover">Materials Leftover (Optional)</Label>
            <Textarea
              id="materials-leftover"
              placeholder='{"shingles": "3 bundles"}'
              value={materialsLeftoverText}
              onChange={(e) => setMaterialsLeftoverText(e.target.value)}
              disabled={isComplete}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">JSON format or plain text</p>
          </div>
        </div>

        {/* Crew Notes */}
        <div className="space-y-2 pt-4 border-t">
          <Label htmlFor="crew-notes">Crew Notes</Label>
          <Textarea
            id="crew-notes"
            placeholder="Any additional notes about the job completion..."
            value={checklist.crew_notes}
            onChange={(e) => setChecklist({ ...checklist, crew_notes: e.target.value })}
            disabled={isComplete}
            rows={4}
          />
        </div>

        {/* Actions */}
        {!isComplete && (
          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} variant="outline" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Progress
            </Button>
            <Button onClick={handleComplete} disabled={saving} className="flex-1">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Mark Complete & Ready for Invoice
            </Button>
          </div>
        )}

        {isComplete && (
          <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-400 font-medium">
              ✓ This job has passed all quality checks and is ready for invoicing.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
